<template>
  <div class="categories-tab">
    <div class="grid">
      <!-- Левая панель: Дерево категорий -->
      <div class="col-12 md:col-6">
        <div class="card">
          <div class="card-header flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h3 class="m-0">Дерево категорий</h3>
            <div class="flex gap-2 flex-wrap">
              <Button
                icon="pi pi-plus"
                label="Создать"
                class="p-button-sm p-button-success"
                :disabled="isCleaningUp"
                @click="showCreateDialog = true"
              />
              <Button
                icon="pi pi-sync"
                label="Синхронизировать"
                class="p-button-sm p-button-primary"
                :loading="isSyncing"
                :disabled="isCleaningUp"
                @click="syncCategories"
              />
              <Button
                :icon="isCleaningUp ? 'pi pi-spin pi-spinner' : 'pi pi-trash'"
                :label="isCleaningUp ? 'Очистка идёт…' : 'Удалить пустые'"
                class="p-button-sm p-button-warning"
                :disabled="isSyncing"
                @click="openCleanupDialog"
              />
            </div>
          </div>

          <!-- Дерево категорий -->
          <Tree
            v-model:expandedKeys="expandedKeys"
            v-model:selectionKeys="selectedKey"
            :value="categoriesTree"
            :loading="loading"
            :lazy="true"
            :loadingKeys="loadingKeys"
            selectionMode="single"
            @node-select="onNodeSelect"
            @node-expand="onNodeExpand"
            :filter="true"
            filterMode="lenient"
            filterPlaceholder="Поиск категорий..."
          >
            <template #default="slotProps">
              <div class="flex align-items-center justify-content-between" style="width: 100%">
                <div class="flex align-items-center">
                  <i 
                    v-if="slotProps.node.data.is_protected" 
                    class="pi pi-lock text-red-500 mr-2" 
                    v-tooltip="'Защищена от удаления при синхронизации'"
                  ></i>
                  <span>{{ slotProps.node.label }}</span>
                </div>
                <div class="flex gap-1">
                  <Button
                    icon="pi pi-pencil"
                    class="p-button-text p-button-sm p-button-rounded"
                    @click.stop="editCategory(slotProps.node.data)"
                  />
                  <Button
                    icon="pi pi-trash"
                    class="p-button-text p-button-sm p-button-rounded p-button-danger"
                    @click.stop="confirmDelete(slotProps.node.data)"
                  />
                </div>
              </div>
            </template>
          </Tree>
        </div>
      </div>

      <!-- Правая панель: Детали категории -->
      <div class="col-12 md:col-6">
        <div class="card">
          <h3 class="mb-3">Детали категории</h3>

          <div v-if="selectedCategory" class="category-details">
            <div class="field">
              <label class="font-bold">ID:</label>
              <span class="ml-2">{{ selectedCategory.category_id }}</span>
            </div>
            <div class="field">
              <label class="font-bold">Название:</label>
              <span class="ml-2">{{ selectedCategory.name }}</span>
            </div>
            <div class="field">
              <label class="font-bold">Slug:</label>
              <span class="ml-2">{{ selectedCategory.slug || '—' }}</span>
            </div>
            <div class="field">
              <label class="font-bold">Активна:</label>
              <Tag :value="selectedCategory.active ? 'Да' : 'Нет'" :severity="selectedCategory.active ? 'success' : 'danger'" class="ml-2" />
            </div>
            <div class="field">
              <label class="font-bold">Глубина:</label>
              <span class="ml-2">{{ selectedCategory.depth }}</span>
            </div>
            <div class="field">
              <label class="font-bold">Защита от синхронизации:</label>
              <Tag 
                v-if="selectedCategory.is_protected" 
                value="Защищена" 
                severity="danger" 
                class="ml-2"
              >
                <template #icon>
                  <i class="pi pi-lock mr-1"></i>
                </template>
              </Tag>
              <Tag v-else value="Нет" severity="secondary" class="ml-2" />
            </div>
            <div v-if="selectedCategory.is_protected && selectedCategory.protection_reason" class="field">
              <label class="font-bold">Причина защиты:</label>
              <span class="ml-2">{{ selectedCategory.protection_reason }}</span>
            </div>
            <div v-if="selectedCategory.description" class="field">
              <label class="font-bold">Описание:</label>
              <p class="mt-2">{{ selectedCategory.description }}</p>
            </div>

            <Divider />

            <h4>Атрибуты ({{ selectedCategory.attribute_links?.length || 0 }})</h4>
            <div v-if="selectedCategory.attribute_links && selectedCategory.attribute_links.length > 0">
              <DataTable :value="selectedCategory.attribute_links" size="small">
                <Column field="attribute_id" header="ID атрибута"></Column>
                <Column field="required" header="Обязательный">
                  <template #body="slotProps">
                    <i :class="slotProps.data.required ? 'pi pi-check text-green-500' : 'pi pi-times text-red-500'"></i>
                  </template>
                </Column>
                <Column field="display_order" header="Порядок"></Column>
              </DataTable>
            </div>
            <div v-else>
              <p class="text-500">Атрибуты не привязаны</p>
            </div>

            <div class="flex gap-2 mt-3">
              <Button
                label="Редактировать"
                icon="pi pi-pencil"
                class="p-button-primary"
                @click="editCategory(selectedCategory)"
              />
              <Button
                label="Удалить"
                icon="pi pi-trash"
                class="p-button-danger"
                @click="confirmDelete(selectedCategory)"
              />
            </div>
          </div>

          <div v-else class="text-center text-500 p-5">
            <i class="pi pi-inbox" style="font-size: 3rem"></i>
            <p class="mt-3">Выберите категорию из дерева</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Диалог создания/редактирования категории -->
    <Dialog
      v-model:visible="showCreateDialog"
      :header="editingCategory ? 'Редактирование категории' : 'Создание категории'"
      :modal="true"
      :style="{ width: '700px' }"
    >
      <div class="category-form">
        <!-- Основная информация -->
        <div class="form-section">
          <h4 class="section-title">Основная информация</h4>
          <div class="form-grid">
            <div class="form-field">
              <label for="name" class="field-label required">Название</label>
              <InputText 
                id="name" 
                v-model="categoryForm.name" 
                :class="{ 'p-invalid': errors.name }"
                class="field-input"
                placeholder="LUXOTTICA"
              />
              <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
            </div>

            <div class="form-field">
              <label for="slug" class="field-label">Slug</label>
              <InputText 
                id="slug" 
                v-model="categoryForm.slug" 
                class="field-input"
                placeholder="ochki-luxottica"
              />
            </div>
          </div>

          <div class="form-field">
            <label for="description" class="field-label">Описание</label>
            <Textarea 
              id="description" 
              v-model="categoryForm.description" 
              rows="3" 
              class="field-input"
              placeholder="Введите описание категории..."
            />
          </div>
        </div>

        <!-- SEO настройки -->
        <div class="form-section">
          <h4 class="section-title">SEO настройки</h4>
          <div class="form-field">
            <label for="meta_title" class="field-label">Meta Title (SEO)</label>
            <InputText 
              id="meta_title" 
              v-model="categoryForm.meta_title" 
              class="field-input"
              placeholder="Введите заголовок для поисковых систем..."
            />
          </div>

          <div class="form-field">
            <label for="meta_description" class="field-label">Meta Description (SEO)</label>
            <Textarea 
              id="meta_description" 
              v-model="categoryForm.meta_description" 
              rows="2" 
              class="field-input"
              placeholder="Введите описание для поисковых систем..."
            />
          </div>
        </div>

        <!-- Изображение категории -->
        <div class="form-section">
          <h4 class="section-title">Изображение категории</h4>
          <div class="image-section">
            <!-- Текущее изображение -->
            <div v-if="editingCategory && (editingCategory.logo_url || editingCategory.logo_file_path)" class="current-image">
              <div class="image-label">
                <i class="pi pi-image text-primary"></i>
                <span class="font-semibold">Текущее изображение:</span>
              </div>
              <div class="image-preview">
                <img 
                  :src="getImageUrl(editingCategory)" 
                  alt="Изображение категории"
                  class="category-image"
                  @error="onImageError"
                />
              </div>
            </div>

            <!-- Сообщение если нет изображения -->
            <div v-else-if="editingCategory" class="no-image">
              <div class="image-label">
                <i class="pi pi-image-plus text-muted"></i>
                <span class="text-muted">Изображение не загружено</span>
              </div>
            </div>
            
            <!-- Загрузка нового изображения -->
            <div class="upload-section">
              <label class="field-label">{{ editingCategory ? 'Загрузить новое изображение' : 'Загрузить изображение' }}</label>
              <FileUpload
                ref="fileUpload"
                mode="basic"
                name="categoryImage"
                accept="image/*"
                :maxFileSize="5000000"
                chooseLabel="Выбрать файл"
                class="file-upload-btn"
                @select="onImageSelect"
                @clear="selectedImage = null"
              />
              <small class="upload-hint">Максимальный размер: 5MB. Форматы: JPG, PNG, GIF</small>
            </div>

            <!-- Предпросмотр нового изображения -->
            <div v-if="selectedImage" class="new-image-preview">
              <div class="image-label">
                <i class="pi pi-eye text-primary"></i>
                <span class="font-semibold">Предпросмотр:</span>
              </div>
              <div class="image-preview">
                <img 
                  :src="imagePreview"
                  alt="Предпросмотр изображения"
                  class="category-image"
                />
              </div>
            </div>

            <!-- Опция синхронизации с PartsSoft -->
            <div class="sync-option">
              <Checkbox id="sync_image" v-model="categoryForm.sync_image_with_partssoft" :binary="true" />
              <label for="sync_image" class="checkbox-label">Синхронизировать изображение с PartsSoft</label>
            </div>
          </div>
        </div>

        <!-- Настройки отображения -->
        <div class="form-section">
          <h4 class="section-title">Настройки отображения</h4>
          <div class="checkbox-group">
            <div class="checkbox-item">
              <Checkbox id="active" v-model="categoryForm.active" :binary="true" />
              <label for="active" class="checkbox-label">Активна</label>
            </div>

            <div class="checkbox-item">
              <Checkbox id="on_main_page" v-model="categoryForm.on_main_page" :binary="true" />
              <label for="on_main_page" class="checkbox-label">Показывать на главной странице</label>
            </div>
          </div>
        </div>

        <!-- Защита от синхронизации -->
        <div class="form-section" v-if="editingCategory">
          <h4 class="section-title">
            <i class="pi pi-shield mr-2"></i>
            Защита категории
          </h4>
          <div class="checkbox-group">
            <div class="checkbox-item">
              <Checkbox id="is_protected" v-model="categoryForm.is_protected" :binary="true" />
              <label for="is_protected" class="checkbox-label">
                <i class="pi pi-lock text-red-500 mr-1"></i>
                Защитить от удаления при синхронизации
              </label>
            </div>
          </div>
          <small class="protection-hint text-orange-500">
            <i class="pi pi-info-circle mr-1"></i>
            Защищённая категория и все её вложенные категории/товары не будут удалены при синхронизации с PartsSoft
          </small>
        </div>
      </div>

      <template #footer>
        <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="showCreateDialog = false" />
        <Button
          :label="editingCategory ? 'Сохранить' : 'Создать'"
          icon="pi pi-check"
          class="p-button-primary"
          :loading="isSaving"
          @click="saveCategory"
        />
      </template>
    </Dialog>

    <!-- Диалог подтверждения удаления -->
    <Dialog
      v-model:visible="showDeleteDialog"
      header="Подтверждение удаления"
      :modal="true"
      :style="{ width: '450px' }"
    >
      <p>Вы уверены, что хотите удалить категорию <strong>{{ categoryToDelete?.name }}</strong>?</p>
      <p class="text-red-500"><i class="pi pi-exclamation-triangle"></i> Это действие нельзя отменить!</p>

      <template #footer>
        <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="showDeleteDialog = false" />
        <Button
          label="Удалить"
          icon="pi pi-trash"
          class="p-button-danger"
          :loading="isDeleting"
          @click="deleteCategory"
        />
      </template>
    </Dialog>

    <!-- Диалог очистки пустых категорий -->
    <Dialog
      v-model:visible="showCleanupDialog"
      header="Очистка пустых категорий"
      :modal="true"
      :closable="true"
      :closeOnEscape="true"
      :style="{ width: '700px' }"
      @hide="onCleanupDialogHide"
    >
      <!-- До начала -->
      <div v-if="!isCleaningUp && !cleanupCompleted">
        <div class="flex align-items-center gap-3 mb-4 p-3 border-round" style="background: #fff3e0;">
          <i class="pi pi-info-circle text-orange-500" style="font-size: 1.5rem"></i>
          <div>
            <p class="m-0 font-semibold text-orange-700">Что будет сделано:</p>
            <ul class="m-0 mt-2 pl-4 text-sm text-orange-900">
              <li>Все конечные категории будут проверены через API PartsSoft</li>
              <li>Категории без товаров и без дочерних будут удалены</li>
              <li>Защищённые и корневые категории не затрагиваются</li>
              <li>Каскадное удаление вверх (если родитель тоже окажется пуст)</li>
            </ul>
          </div>
        </div>
        <p class="text-600">Все кнопки синхронизации будут заблокированы на время выполнения.</p>
      </div>

      <!-- В процессе -->
      <div v-if="isCleaningUp">
        <div class="mb-3">
          <div class="flex justify-content-between align-items-center mb-2">
            <span class="font-semibold text-primary">
              <i class="pi pi-spin pi-spinner mr-2"></i>
              {{ cleanupProgress.message || 'Подготовка...' }}
            </span>
            <span class="text-sm text-600">
              {{ cleanupProgress.checked || 0 }} / {{ cleanupProgress.total || 0 }}
            </span>
          </div>
          <ProgressBar 
            :value="cleanupProgressPercent" 
            :showValue="true"
            style="height: 1.5rem"
          />
        </div>

        <!-- Статистика -->
        <div class="grid mt-3">
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #e3f2fd;">
              <div class="text-2xl font-bold text-blue-600">{{ cleanupProgress.checked || 0 }}</div>
              <div class="text-xs text-blue-800 mt-1">Проверено</div>
            </div>
          </div>
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #ffebee;">
              <div class="text-2xl font-bold text-red-600">{{ cleanupProgress.deleted || 0 }}</div>
              <div class="text-xs text-red-800 mt-1">Удалено</div>
            </div>
          </div>
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #e8f5e9;">
              <div class="text-2xl font-bold text-green-600">{{ cleanupProgress.skipped || 0 }}</div>
              <div class="text-xs text-green-800 mt-1">Пропущено</div>
            </div>
          </div>
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #fff3e0;">
              <div class="text-2xl font-bold text-orange-600">{{ cleanupProgress.errors || 0 }}</div>
              <div class="text-xs text-orange-800 mt-1">Ошибок</div>
            </div>
          </div>
        </div>

        <!-- Последние удалённые -->
        <div v-if="cleanupProgress.deleted_details && cleanupProgress.deleted_details.length > 0" class="mt-3">
          <p class="text-sm font-semibold text-600 mb-2">Последние удалённые:</p>
          <div class="cleanup-log">
            <div 
              v-for="item in cleanupProgress.deleted_details" 
              :key="item.id"
              class="flex align-items-center gap-2 text-sm py-1 px-2 border-round mb-1"
              style="background: #ffebee;"
            >
              <i class="pi pi-trash text-red-400"></i>
              <span class="text-red-700">{{ item.name }}</span>
              <Tag :value="'depth: ' + item.depth" severity="secondary" class="ml-auto" style="font-size: 0.7rem" />
            </div>
          </div>
        </div>
      </div>

      <!-- Завершено -->
      <div v-if="cleanupCompleted && !isCleaningUp">
        <div class="flex align-items-center gap-3 mb-4 p-3 border-round" :style="cleanupProgress.deleted > 0 ? 'background: #e8f5e9' : 'background: #e3f2fd'">
          <i :class="cleanupProgress.deleted > 0 ? 'pi pi-check-circle text-green-500' : 'pi pi-info-circle text-blue-500'" style="font-size: 2rem"></i>
          <div>
            <p class="m-0 font-bold" :class="cleanupProgress.deleted > 0 ? 'text-green-700' : 'text-blue-700'">
              {{ cleanupProgress.deleted > 0 ? 'Очистка завершена!' : 'Пустых категорий не найдено' }}
            </p>
            <p class="m-0 mt-1 text-sm text-600">{{ cleanupProgress.message }}</p>
          </div>
        </div>

        <!-- Итоговая статистика -->
        <div class="grid">
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #e3f2fd;">
              <div class="text-2xl font-bold text-blue-600">{{ cleanupProgress.checked || 0 }}</div>
              <div class="text-xs text-blue-800 mt-1">Проверено</div>
            </div>
          </div>
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #ffebee;">
              <div class="text-2xl font-bold text-red-600">{{ cleanupProgress.deleted || 0 }}</div>
              <div class="text-xs text-red-800 mt-1">Удалено</div>
            </div>
          </div>
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #e8f5e9;">
              <div class="text-2xl font-bold text-green-600">{{ cleanupProgress.skipped || 0 }}</div>
              <div class="text-xs text-green-800 mt-1">Пропущено</div>
            </div>
          </div>
          <div class="col-3">
            <div class="text-center p-2 border-round" style="background: #fff3e0;">
              <div class="text-2xl font-bold text-orange-600">{{ cleanupProgress.errors || 0 }}</div>
              <div class="text-xs text-orange-800 mt-1">Ошибок</div>
            </div>
          </div>
        </div>

        <!-- Все удалённые категории -->
        <div v-if="cleanupProgress.deleted_details && cleanupProgress.deleted_details.length > 0" class="mt-3">
          <p class="text-sm font-semibold text-600 mb-2">Удалённые категории ({{ cleanupProgress.deleted_details.length }}):</p>
          <div class="cleanup-log cleanup-log-final">
            <div 
              v-for="item in cleanupProgress.deleted_details" 
              :key="item.id"
              class="flex align-items-center gap-2 text-sm py-1 px-2 border-round mb-1"
              style="background: #ffebee;"
            >
              <i class="pi pi-trash text-red-400"></i>
              <span class="text-red-700">{{ item.name }}</span>
              <Tag :value="'depth: ' + item.depth" severity="secondary" class="ml-auto" style="font-size: 0.7rem" />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-content-between w-full">
          <div>
            <span v-if="isCleaningUp" class="text-sm text-500">
              <i class="pi pi-info-circle mr-1"></i>Очистка идёт в фоне. Синхронизация заблокирована.
            </span>
          </div>
          <div class="flex gap-2">
            <Button
              v-if="isCleaningUp"
              label="Свернуть"
              icon="pi pi-window-minimize"
              class="p-button-text"
              @click="showCleanupDialog = false"
            />
            <Button
              v-if="isCleaningUp"
              label="Остановить"
              icon="pi pi-stop"
              class="p-button-danger p-button-outlined"
              @click="stopCleanup"
            />
            <Button
              v-if="!isCleaningUp"
              label="Закрыть"
              icon="pi pi-times"
              class="p-button-text"
              @click="showCleanupDialog = false"
            />
            <Button
              v-if="!isCleaningUp && !cleanupCompleted"
              label="Начать очистку"
              icon="pi pi-play"
              class="p-button-warning"
              @click="startCleanup"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import Tree from 'primevue/tree'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Checkbox from 'primevue/checkbox'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Divider from 'primevue/divider'
import FileUpload from 'primevue/fileupload'
import ProgressBar from 'primevue/progressbar'
import apiService from '../../services/apiService'

// Props
const props = defineProps({
  storeId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['cleanup-status-change'])

const toast = useToast()

// State
const loading = ref(false)
const isSyncing = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)

const categoriesTree = ref([])
const expandedKeys = ref({})
const loadingKeys = ref({})
const selectedKey = ref(null)
const selectedCategory = ref(null)

const showCreateDialog = ref(false)
const showDeleteDialog = ref(false)
const showCleanupDialog = ref(false)
const isCleaningUp = ref(false)
const cleanupCompleted = ref(false)
const cleanupProgress = ref({
  stage: '',
  total: 0,
  total_all: 0,
  checked: 0,
  deleted: 0,
  skipped: 0,
  errors: 0,
  message: '',
  current_category: '',
  deleted_details: []
})
const editingCategory = ref(null)
const categoryToDelete = ref(null)

const categoryForm = ref({
  name: '',
  slug: '',
  description: '',
  meta_title: '',
  meta_description: '',
  active: true,
  on_main_page: false,
  sync_image_with_partssoft: false
})

// Переменные для работы с изображениями
const selectedImage = ref(null)
const imagePreview = ref(null)
const fileUpload = ref(null)

const errors = ref({})

// Работа с деревом категорий (ленивая загрузка)
const loadedNodeKeys = new Set() // Узлы, для которых дочерние категории уже загружены

const setNodeLoading = (key, isLoading) => {
  if (isLoading) {
    loadingKeys.value = {
      ...loadingKeys.value,
      [key]: true
    }
  } else {
    const { [key]: _removed, ...rest } = loadingKeys.value
    loadingKeys.value = rest
  }
}

const createTreeNodes = (items) => {
  return items.map((item) => ({
    key: item.category.category_id.toString(),
    label: item.category.name,
    data: item.category,
    children: [],
    leaf: item.children_count === 0
  }))
}

const fetchCategories = async (parentId = null) => {
  const params = { store_id: props.storeId }
  if (parentId !== null && parentId !== undefined) {
    params.parent_id = parentId
  }
  const response = await apiService.get('/sync/categories', { params })
  return response.data
}

const loadRootCategories = async () => {
  loading.value = true
  try {
    const data = await fetchCategories()
    categoriesTree.value = createTreeNodes(data)
    expandedKeys.value = {}
    loadingKeys.value = {}
    loadedNodeKeys.clear()
    selectedKey.value = null
    selectedCategory.value = null
  } catch (error) {
    console.error('Ошибка загрузки категорий:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить категории',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const onNodeExpand = async (event) => {
  const node = event?.node ?? event
  if (!node || loadedNodeKeys.has(node.key) || node.leaf) {
    return
  }

  setNodeLoading(node.key, true)
  try {
    const children = await fetchCategories(node.data.category_id)
    const childNodes = createTreeNodes(children)
    node.children = childNodes
    node.leaf = childNodes.length === 0
    loadedNodeKeys.add(node.key)
    if (childNodes.length === 0) {
      const updatedExpanded = { ...expandedKeys.value }
      delete updatedExpanded[node.key]
      expandedKeys.value = updatedExpanded
    }
  } catch (error) {
    console.error('Ошибка загрузки дочерних категорий:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить дочерние категории',
      life: 3000
    })
    const updatedExpanded = { ...expandedKeys.value }
    delete updatedExpanded[node.key]
    expandedKeys.value = updatedExpanded
  } finally {
    setNodeLoading(node.key, false)
  }
}

// Выбор узла
const onNodeSelect = async (event) => {
  const node = event?.node ?? event
  if (!node) {
    return
  }
  try {
    const response = await apiService.get(`/sync/categories/${node.data.category_id}`, {
      params: { store_id: props.storeId }
    })
    selectedCategory.value = response.data
  } catch (error) {
    console.error('Ошибка загрузки деталей категории:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить детали категории',
      life: 3000
    })
  }
}

// Синхронизация категорий
const syncCategories = async () => {
  isSyncing.value = true
  try {
    const response = await apiService.post('/sync/categories/sync', {}, {
      params: { store_id: props.storeId }
    })
    toast.add({
      severity: 'success',
      summary: 'Синхронизация завершена',
      detail: response.data.message,
      life: 5000
    })
    await loadRootCategories()
  } catch (error) {
    console.error('Ошибка синхронизации:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка синхронизации',
      detail: error.response?.data?.detail || error.message,
      life: 5000
    })
  } finally {
    isSyncing.value = false
  }
}

// Редактирование категории
const editCategory = (category) => {
  editingCategory.value = category
  categoryForm.value = {
    name: category.name,
    slug: category.slug || '',
    description: category.description || '',
    meta_title: category.meta_title || '',
    meta_description: category.meta_description || '',
    active: category.active,
    on_main_page: category.on_main_page,
    sync_image_with_partssoft: category.sync_image_with_partssoft || false,
    is_protected: category.is_protected || false
  }
  
  // Сбросить состояние изображения
  selectedImage.value = null
  imagePreview.value = null
  if (fileUpload.value) {
    fileUpload.value.clear()
  }
  
  showCreateDialog.value = true
}

// Сохранение категории
const saveCategory = async () => {
  errors.value = {}
  
  if (!categoryForm.value.name) {
    errors.value.name = 'Название обязательно'
    return
  }
  
  isSaving.value = true
  try {
    let savedCategory = null
    
    if (editingCategory.value) {
      // Обновление
      const response = await apiService.put(
        `/sync/categories/${editingCategory.value.category_id}`,
        categoryForm.value,
        { params: { store_id: props.storeId } }
      )
      savedCategory = response.data
      
      toast.add({
        severity: 'success',
        summary: 'Успех',
        detail: 'Категория обновлена',
        life: 3000
      })
    } else {
      // Создание
      const response = await apiService.post(
        '/sync/categories',
        categoryForm.value,
        { params: { store_id: props.storeId } }
      )
      savedCategory = response.data
      
      toast.add({
        severity: 'success',
        summary: 'Успех',
        detail: 'Категория создана',
        life: 3000
      })
    }
    
    // Загрузка изображения если выбрано
    if (selectedImage.value && savedCategory) {
      try {
        await uploadCategoryImage(savedCategory.category_id)
        toast.add({
          severity: 'success',
          summary: 'Изображение загружено',
          detail: 'Изображение категории успешно загружено',
          life: 3000
        })
      } catch (imageError) {
        console.error('Ошибка загрузки изображения:', imageError)
        toast.add({
          severity: 'warn',
          summary: 'Частичный успех',
          detail: 'Категория сохранена, но изображение не загружено',
          life: 5000
        })
      }
    }
    
    showCreateDialog.value = false
    editingCategory.value = null
    selectedImage.value = null
    imagePreview.value = null
    if (fileUpload.value) {
      fileUpload.value.clear()
    }
    await loadRootCategories()
  } catch (error) {
    console.error('Ошибка сохранения:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сохранить категорию',
      life: 5000
    })
  } finally {
    isSaving.value = false
  }
}

// Подтверждение удаления
const confirmDelete = (category) => {
  categoryToDelete.value = category
  showDeleteDialog.value = true
}

// Удаление категории
const deleteCategory = async () => {
  isDeleting.value = true
  try {
    await apiService.delete(`/sync/categories/${categoryToDelete.value.category_id}`, {
      params: { store_id: props.storeId }
    })
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Категория удалена',
      life: 3000
    })
    
    showDeleteDialog.value = false
    categoryToDelete.value = null
    selectedCategory.value = null
    await loadRootCategories()
  } catch (error) {
    console.error('Ошибка удаления:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось удалить категорию',
      life: 5000
    })
  } finally {
    isDeleting.value = false
  }
}

// Функции для работы с изображениями
const onImageSelect = (event) => {
  const file = event.files[0]
  if (file) {
    selectedImage.value = file
    
    // Создать предпросмотр
    const reader = new FileReader()
    reader.onload = (e) => {
      imagePreview.value = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

const getImageUrl = (category) => {
  if (category?.logo_url) {
    return category.logo_url
  }
  if (category?.logo_file_path) {
    // Проверяем, начинается ли путь с 'uploads/'
    const path = category.logo_file_path.replace(/\\/g, '/')
    let finalUrl
    if (path.startsWith('uploads/')) {
      finalUrl = `/api/${path}`
    } else {
      finalUrl = `/api/uploads/${path}`
    }
    return finalUrl
  }
  return null
}

const onImageError = (event) => {
  console.error('Image failed to load:', event.target.src)
  console.error('Error event:', event)
  // Скрыть изображение если не удалось загрузить
  event.target.style.display = 'none'
}

const uploadCategoryImage = async (categoryId) => {
  if (!selectedImage.value) return null

  try {
    const formData = new FormData()
    formData.append('file', selectedImage.value)
    formData.append('sync_with_partssoft', categoryForm.value.sync_image_with_partssoft)

    const response = await apiService.post(
      `/sync/categories/${categoryId}/upload-image?store_id=${props.storeId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data
  } catch (error) {
    console.error('Ошибка загрузки изображения:', error)
    throw error
  }
}

// === Очистка пустых категорий (фоновый процесс + polling) ===

const cleanupProgressPercent = computed(() => {
  if (!cleanupProgress.value.total || cleanupProgress.value.total === 0) return 0
  return Math.round((cleanupProgress.value.checked / cleanupProgress.value.total) * 100)
})

let cleanupPollingInterval = null

/**
 * Запросить прогресс у сервера и обновить UI.
 * Если очистка завершена/сбоилась — остановить polling.
 */
const fetchCleanupProgress = async () => {
  try {
    const response = await apiService.get(`/sync/categories/cleanup-empty/progress`, {
      params: { store_id: props.storeId }
    })
    const data = response.data
    if (!data || data.stage === 'idle') {
      // Процесс не найден — возможно был очищен
      stopCleanupPolling()
      isCleaningUp.value = false
      return
    }

    cleanupProgress.value = { ...cleanupProgress.value, ...data }

    if (data.stage === 'completed' || data.stage === 'failed') {
      stopCleanupPolling()
      isCleaningUp.value = false
      cleanupCompleted.value = true
      emit('cleanup-status-change', false)

      if (data.stage === 'completed' && data.deleted > 0) {
        toast.add({
          severity: 'success',
          summary: 'Очистка завершена',
          detail: `Удалено ${data.deleted} пустых категорий`,
          life: 5000
        })
        await loadRootCategories()
      } else if (data.stage === 'completed') {
        toast.add({
          severity: 'info',
          summary: 'Очистка завершена',
          detail: 'Пустых категорий не найдено',
          life: 3000
        })
      } else {
        toast.add({
          severity: 'error',
          summary: 'Ошибка очистки',
          detail: data.message || data.error || 'Произошла ошибка',
          life: 5000
        })
      }
    }
  } catch (err) {
    console.error('Ошибка получения прогресса очистки:', err)
  }
}

const startCleanupPolling = () => {
  stopCleanupPolling()
  cleanupPollingInterval = setInterval(fetchCleanupProgress, 2000)
}

const stopCleanupPolling = () => {
  if (cleanupPollingInterval) {
    clearInterval(cleanupPollingInterval)
    cleanupPollingInterval = null
  }
}

const startCleanup = async () => {
  isCleaningUp.value = true
  cleanupCompleted.value = false
  emit('cleanup-status-change', true)
  cleanupProgress.value = {
    stage: 'starting',
    total: 0,
    total_all: 0,
    checked: 0,
    deleted: 0,
    skipped: 0,
    errors: 0,
    message: 'Подготовка...',
    current_category: '',
    deleted_details: []
  }

  try {
    const response = await apiService.post(`/sync/categories/cleanup-empty/start`, null, {
      params: { store_id: props.storeId }
    })

    if (response.data?.status === 'queued') {
      toast.add({
        severity: 'info',
        summary: 'Очистка запущена',
        detail: `${response.data.total_to_check} категорий для проверки`,
        life: 3000
      })
      // Автоматически открываем диалог и начинаем polling
      showCleanupDialog.value = true
      startCleanupPolling()
    }
  } catch (error) {
    console.error('Ошибка запуска очистки:', error)
    isCleaningUp.value = false
    cleanupCompleted.value = false
    emit('cleanup-status-change', false)
    const detail = error.response?.data?.detail || error.message || 'Не удалось запустить очистку'
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail,
      life: 5000
    })
  }
}

const stopCleanup = async () => {
  try {
    await apiService.post(`/sync/categories/cleanup-empty/stop`, null, {
      params: { store_id: props.storeId }
    })
    toast.add({
      severity: 'warn',
      summary: 'Остановка',
      detail: 'Запрос на остановку очистки отправлен',
      life: 3000
    })
  } catch (err) {
    console.error('Ошибка остановки очистки:', err)
  }
}

/**
 * Открыть диалог очистки.
 * Если процесс уже идёт — показываем его текущий прогресс и при необходимости
 * возобновляем polling (например, после сворачивания окна или возврата на страницу).
 */
const openCleanupDialog = () => {
  showCleanupDialog.value = true
  if (isCleaningUp.value && !cleanupPollingInterval) {
    startCleanupPolling()
  }
}

/**
 * Обработчик закрытия диалога (крестик / Esc / кнопки «Свернуть» и «Закрыть»).
 * - Пока очистка идёт — просто сворачиваем окно, фоновый процесс продолжается,
 *   а повторное нажатие «Удалить пустые» снова откроет его прогресс.
 * - После завершения — чистим прогресс на сервере и сбрасываем состояние.
 */
const onCleanupDialogHide = async () => {
  if (isCleaningUp.value) {
    return
  }
  if (cleanupCompleted.value) {
    cleanupCompleted.value = false
    cleanupProgress.value = {
      stage: '',
      total: 0,
      total_all: 0,
      checked: 0,
      deleted: 0,
      skipped: 0,
      errors: 0,
      message: '',
      current_category: '',
      deleted_details: []
    }
    // Очищаем прогресс на сервере
    try {
      await apiService.post(`/sync/categories/cleanup-empty/clear`, null, {
        params: { store_id: props.storeId }
      })
    } catch (e) {
      // При ошибке просто игнорируем
    }
  }
}

/**
 * При загрузке страницы — проверяем, не запущена ли уже очистка.
 * Если да — восстанавливаем UI и начинаем polling.
 */
const checkOngoingCleanup = async () => {
  try {
    const response = await apiService.get(`/sync/categories/cleanup-empty/progress`, {
      params: { store_id: props.storeId }
    })
    const data = response.data
    if (data && data.is_running) {
      // Очистка уже идёт — восстанавливаем состояние и polling, но НЕ открываем
      // диалог принудительно. Кнопка «Удалить пустые» покажет «Очистка идёт…»,
      // и пользователь сам откроет прогресс по нажатию.
      isCleaningUp.value = true
      cleanupCompleted.value = false
      cleanupProgress.value = { ...cleanupProgress.value, ...data }
      emit('cleanup-status-change', true)
      startCleanupPolling()
    }
  } catch (e) {
    // Не критично
  }
}

// Отслеживание изменения storeId
watch(() => props.storeId, () => {
  stopCleanupPolling()
  isCleaningUp.value = false
  cleanupCompleted.value = false
  loadRootCategories()
  checkOngoingCleanup()
})

onMounted(() => {
  loadRootCategories()
  // Проверяем, не запущена ли уже очистка (переживает перезагрузку страницы)
  checkOngoingCleanup()
})

onBeforeUnmount(() => {
  stopCleanupPolling()
})
</script>

<style scoped>
.categories-tab {
  min-height: 600px;
}

.card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.card-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.category-details .field {
  margin-bottom: 1rem;
}

.category-details .field label {
  display: inline-block;
  min-width: 100px;
}

/* Стили для формы редактирования */
.category-form {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;
}

.form-section {
  margin-bottom: 2rem;
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.section-title {
  margin: 0 0 1.5rem 0;
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #007bff;
  display: inline-block;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  font-weight: 600;
  color: #495057;
  min-width: 140px;
  font-size: 0.95rem;
}

.field-label.required::after {
  content: " *";
  color: #dc3545;
}

.field-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.field-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  outline: 0;
}

.field-input.p-invalid {
  border-color: #dc3545;
}

/* Стили для изображений */
.image-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.current-image,
.new-image-preview,
.no-image {
  padding: 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.no-image {
  text-align: center;
  padding: 2rem 1rem;
  border-style: dashed;
}

.no-image .image-label {
  justify-content: center;
  margin-bottom: 0;
  color: #6c757d;
}

.image-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #495057;
}

.image-preview {
  display: flex;
  justify-content: center;
}

.category-image {
  max-width: 200px;
  max-height: 150px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.file-upload-btn {
  align-self: flex-start;
}

.upload-hint {
  color: #6c757d;
  font-size: 0.85rem;
  font-style: italic;
}

.sync-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

/* Стили для чекбоксов */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  transition: background-color 0.15s ease-in-out;
}

.checkbox-item:hover {
  background: #f8f9fa;
}

.checkbox-label {
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  user-select: none;
}

/* Адаптивность */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .form-section {
    padding: 1rem;
  }
}

:deep(.p-tree) {
  border: none;
  padding: 0;
}

:deep(.p-tree-filter-container) {
  margin-bottom: 1rem;
}

/* Стили для файлового загрузчика */
:deep(.p-fileupload-basic) {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  color: #495057;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
}

:deep(.p-fileupload-basic:hover) {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #212529;
}

:deep(.p-fileupload-basic:focus) {
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.25);
  border-color: #6c757d;
}

/* Стили для чекбоксов PrimeVue */
:deep(.p-checkbox) {
  width: 18px;
  height: 18px;
}

:deep(.p-checkbox .p-checkbox-box) {
  border: 2px solid #ced4da;
  border-radius: 3px;
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

:deep(.p-checkbox .p-checkbox-box:hover) {
  border-color: #007bff;
}

:deep(.p-checkbox.p-checkbox-checked .p-checkbox-box) {
  background: #007bff;
  border-color: #007bff;
}

:deep(.p-checkbox.p-checkbox-checked .p-checkbox-box .p-checkbox-icon) {
  color: white;
}

/* Стили для скроллбара */
.category-form::-webkit-scrollbar {
  width: 6px;
}

.category-form::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.category-form::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.category-form::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Стили для секции защиты */
.protection-hint {
  display: block;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  line-height: 1.4;
}

/* Стили для лога очистки */
.cleanup-log {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.5rem;
  background: #fafafa;
}

.cleanup-log-final {
  max-height: 250px;
}

.cleanup-log::-webkit-scrollbar {
  width: 5px;
}

.cleanup-log::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.cleanup-log::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
</style>
